/**
 * taskflow-triage-queue.ts
 *
 * Distraction-Free Triage Review Queue Engine for rapid task processing.
 * Supports J/K traversal, 1-5 direct status keys, P priority cycling, and D deferrals.
 */

import type { TaskFlowItem, TaskFlowPriority } from '../contracts/taskflow-schema.js';

export type TriageAction =
  | 'SET_BACKLOG'
  | 'SET_IN_PROGRESS'
  | 'SET_BLOCKED'
  | 'SET_REVIEW'
  | 'SET_DONE'
  | 'CYCLE_PRIORITY'
  | 'DEFER_ONE_DAY'
  | 'DEFER_ONE_WEEK'
  | 'ARCHIVE';

const PRIORITY_CYCLE: TaskFlowPriority[] = ['low', 'normal', 'medium', 'high', 'urgent'];

export class TaskFlowTriageQueue {
  private items: TaskFlowItem[];
  private currentIndex: number = 0;

  constructor(initialItems: TaskFlowItem[] = []) {
    this.items = [...initialItems];
  }

  getCurrentIndex(): number {
    return this.currentIndex;
  }

  getCurrentItem(): TaskFlowItem | undefined {
    return this.items[this.currentIndex];
  }

  getItems(): TaskFlowItem[] {
    return [...this.items];
  }

  setItems(newItems: TaskFlowItem[]): void {
    this.items = [...newItems];
    if (this.currentIndex >= this.items.length) {
      this.currentIndex = Math.max(0, this.items.length - 1);
    }
  }

  next(): void {
    if (this.currentIndex < this.items.length - 1) {
      this.currentIndex++;
    }
  }

  prev(): void {
    if (this.currentIndex > 0) {
      this.currentIndex--;
    }
  }

  getProgress(): { current: number; total: number; percent: number } {
    const total = this.items.length;
    if (total === 0) return { current: 0, total: 0, percent: 100 };
    const current = this.currentIndex + 1;
    const percent = Math.round((current / total) * 100);
    return { current, total, percent };
  }

  updateCurrentItem(patch: Partial<TaskFlowItem>): void {
    if (this.items[this.currentIndex]) {
      this.items[this.currentIndex] = {
        ...this.items[this.currentIndex],
        ...patch,
      };
    }
  }

  /**
   * Translates keyboard key to TaskFlowItem patch
   */
  handleKeyAction(
    key: string,
    options: { baseDate?: string } = {}
  ): Partial<TaskFlowItem> | null {
    const current = this.getCurrentItem();
    if (!current) return null;

    const upperKey = key.toUpperCase();

    // 1-5 Status assignments
    if (upperKey === '1') return { status: 'backlog' };
    if (upperKey === '2') return { status: 'in-progress' };
    if (upperKey === '3') return { status: 'blocked' };
    if (upperKey === '4') return { status: 'review' };
    if (upperKey === '5') return { status: 'done', progressPercentage: 100 };

    // P = Priority cycle
    if (upperKey === 'P') {
      const curPriority = current.priority || 'normal';
      const idx = PRIORITY_CYCLE.indexOf(curPriority);
      const nextPriority = PRIORITY_CYCLE[(idx + 1) % PRIORITY_CYCLE.length];
      return { priority: nextPriority };
    }

    // D = Defer due date +1d
    if (upperKey === 'D') {
      const base = options.baseDate || current.dueDate || new Date().toISOString().split('T')[0];
      const ms = new Date(base).getTime();
      const nextDay = new Date(ms + 24 * 60 * 60 * 1000).toISOString().split('T')[0];
      return { dueDate: nextDay };
    }

    // A = Archive
    if (upperKey === 'A') {
      return { status: 'archived' };
    }

    return null;
  }
}
